import { setYaxisRangeBasedOnTracesVisibility } from 'helpers/plotly/yAxisRangeCalculator'
import moment from 'moment-timezone'
import { color } from 'styles/colors'
import _ from 'underscore'
import { AwairRuntimeHistory } from 'types/AwairRuntimeHistory'
import { createAwairRthTrace } from 'pages/Device/AwairRthPanelNew/utils'

const LEFT_YAXIS = 'y'
const RIGHT_YAXIS = 'y2'

interface Line {
  color: string;
  dash: Plotly.Dash;
  width: number | undefined;
}
interface AwairItems {
  temperature: Line,
  humidity: Line,
  co2: Line,
  voc: Line,
  pm25: Line,
  actualAirflow: Line
}
const awairPlot: AwairItems = {
  temperature: {
    color: color('awairTemp'),
    dash: 'solid',
    width: 2
  },
  humidity: {
    color: color('awairHumidity'),
    dash: 'solid',
    width: 2
  },
  co2: {
    color: color('awairCo2'),
    dash: 'solid',
    width: 2
  },
  voc: {

    color: color('awairVoc'),
    dash: 'solid',
    width: 2
  },
  pm25: {
    color: color('awairPm25'),
    dash: 'solid',
    width: 2
  },
  actualAirflow: {
    color: color('indoorUnitActualAirflow'),
    dash: 'longdashdot',
    width: 2
  }
}

const baseLayout: Partial<Plotly.Layout> = {
  font: {
    family: 'Inter, sans-serif',
    size: 11,
    color: color('rthChartAxisLabel')
  },
  showlegend: false,
  margin: { t: 20, b: 30, l: 75, r: 75, pad: 0 },
  hovermode: 'x',
  hoverdistance: 50000, // Added as maximum distance to trigger all mouse events properly.
  xaxis: {
    zeroline: false,
    ticklen: 8,
    showgrid: false,
    autorange: false,
    linecolor: color('rthChartBorder'),
    linewidth: 1,
    nticks: 12,
    mirror: true,
    showline: true,
    tickcolor: color('rthChartBorder'),
    tickformatstops: [{
      dtickrange: [null, 1000],
      value: '%-I:%M:%S.%L %p'
    },
    {
      dtickrange: [1000, 60000],
      value: '%-I:%M:%S %p'
    },
    {
      dtickrange: [60000, 3600000],
      value: '%-I:%M %p'
    },
    {
      dtickrange: [3600000, 86400000],
      value: '%-I %p'
    }],
    type: 'date'
  },
  'xaxis.autorange': false,
  'yaxis.autorange': false,
  yaxis: {
    title: 'Temperature °F | Relative Humidity % | µg/m³',
    anchor: 'x',
    fixedrange: true,
    showline: true,
    linecolor: color('rthChartBorder'),
    gridcolor: color('rthChartBorder'),
    zeroline: false,
    mirror: true
  },
  yaxis2: {
    title: 'CFM | PPM | PPB',
    gridcolor: color('rthChartBorder'),
    overlaying: 'y',
    fixedrange: true,
    showline: false,
    zeroline: false,
    side: 'right'
  }
}

export const updateLayoutOnResetZoom = (isZoomed, updatedLayout, initialLayout) => {
  const clonedInitialLayout = JSON.parse(JSON.stringify(initialLayout))

  if (isZoomed && updatedLayout) {
    clonedInitialLayout.xaxis.range = [...updatedLayout.xaxis.range]
    return clonedInitialLayout
  } else {
    return clonedInitialLayout
  }
}

export const buildAwairLayout = (fromTime, toTime, traces) => {
  const range = [
    moment(fromTime).format('YYYY-MM-DDTHH:mm:ss.SSS'),
    moment(toTime).format('YYYY-MM-DDTHH:mm:ss.SSS')
  ]

  const layout = JSON.parse(JSON.stringify(baseLayout)) // deepclone layout object

  setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis, LEFT_YAXIS)
  setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis2, RIGHT_YAXIS)
  return { ...layout, xaxis: { ...layout.xaxis, range } }
}

export const buildTraces = (awairEvents: AwairRuntimeHistory['series'], displayIndoorUnit: boolean, timeZone: string) => {
  const celsiusToFahrenheit = (value) => value != null ? (value * (9 / 5)) + 32 : null

  const temperature = createAwairRthTrace(awairEvents.temperature, timeZone, celsiusToFahrenheit)
  const humidity = createAwairRthTrace(awairEvents.humidity, timeZone)
  const co2 = createAwairRthTrace(awairEvents.co2, timeZone)
  const voc = createAwairRthTrace(awairEvents.voc, timeZone)
  const pm25 = createAwairRthTrace(awairEvents.pm25, timeZone)
  const airflow = createAwairRthTrace(awairEvents.airflow, timeZone)

  const traces = [
    createPlotlyTrace(temperature.occurrences, temperature.values, LEFT_YAXIS, awairPlot.temperature, 'temp'),
    createPlotlyTrace(humidity.occurrences, humidity.values, LEFT_YAXIS, awairPlot.humidity, 'humidity'),
    createPlotlyTrace(co2.occurrences, co2.values, RIGHT_YAXIS, awairPlot.co2, 'co2'),
    createPlotlyTrace(voc.occurrences, voc.values, RIGHT_YAXIS, awairPlot.voc, 'voc'),
    createPlotlyTrace(pm25.occurrences, pm25.values, LEFT_YAXIS, awairPlot.pm25, 'pm25')
  ]

  if (displayIndoorUnit && airflow.occurrences.length > 0) {
    traces.push(createPlotlyTrace(airflow.occurrences, airflow.values, RIGHT_YAXIS, awairPlot.actualAirflow, 'actualAirflow'))
  }

  return traces
}

export const updateTracesVisibility = (traces, selectedLegendItems) => {
  return _.map(traces, (trace) => {
    // using width to control visibility because when we mouse hover we can still access those points
    return { ...trace, line: { ...trace.line, width: selectedLegendItems[trace.name] ? 2 : 0 } }
  })
}

const createPlotlyTrace = (x: Array<string>, y: Array<number | null>, yaxis: string, line: Line, name: string): Partial<Plotly.PlotData> => {
  return {
    x,
    y,
    name,
    yaxis,
    line,
    xaxis: 'x',
    visible: true,
    cliponaxis: false,
    type: 'scatter',
    mode: 'lines',
    width: 0,
    marker: {},
    hoverinfo: 'none',
    connectgaps: true
  }
}
